import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import axios from 'axios';
import { collection, getDocs, query, where, doc, setDoc, orderBy, limit, startAfter, Timestamp } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust the import to match your Firebase configuration
import '../style/Adminorders.css';

const OngoingOrders = () => {
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]); // Store all fetched orders
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [creatingDuplicate, setCreatingDuplicate] = useState(false);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [showAllOrders, setShowAllOrders] = useState(false); // Toggle for showing all orders
  const ORDERS_PER_BATCH = 50; // Increased batch size
  const [markingAsShipped, setMarkingAsShipped] = useState(false);

  // Fetch all users first (needed for brands and user data)
  const fetchAllUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = {};
      const brands = new Set();

      usersSnapshot.docs.forEach(userDoc => {
        const userData = userDoc.data();
        const brandName = userData.brandName || userData.brand_name || userData.name || 'Unknown Brand';
        brands.add(brandName);
        
        users[userDoc.id] = {
          ...userData,
          brandName
        };
      });

      setAllUsers(users);
      setAvailableBrands(Array.from(brands).sort());
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Get date filter for initial load (last 7 days instead of 1 day)
  const getDateFilter = () => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 2);
    return Timestamp.fromDate(sevenDaysAgo);
  };

  // Fetch ALL orders for comprehensive search (no date filter, no pagination limit)
  const fetchAllOrdersForSearch = async () => {
    try {
      setLoading(true);
      const allOrdersData = [];
      const userIds = Object.keys(allUsers);
      
      for (const userId of userIds) {
        try {
          // Fetch ALL confirmed orders for this user (no limit, no date filter)
          const ordersQuery = query(
            collection(db, 'users', userId, 'orders'),
            where('status', '==', 'confirmed'),
            orderBy('updatedAt', 'desc')
          );

          const ordersSnapshot = await getDocs(ordersQuery);
          
          ordersSnapshot.docs.forEach(orderDoc => {
            const orderData = orderDoc.data();
            allOrdersData.push({
              id: orderDoc.id,
              userId,
              brandName: allUsers[userId]?.brandName || 'Unknown Brand',
              ...orderData,
            });
          });
        } catch (userError) {
          // If updatedAt field doesn't exist, try with order_date
          console.warn(`updatedAt not found for user ${userId}, trying with order_date`);
          
          try {
            const fallbackQuery = query(
              collection(db, 'users', userId, 'orders'),
              where('status', '==', 'confirmed'),
              orderBy('order_date', 'desc')
            );

            const fallbackSnapshot = await getDocs(fallbackQuery);
            
            fallbackSnapshot.docs.forEach(orderDoc => {
              const orderData = orderDoc.data();
              allOrdersData.push({
                id: orderDoc.id,
                userId,
                brandName: allUsers[userId]?.brandName || 'Unknown Brand',
                ...orderData,
              });
            });
          } catch (fallbackError) {
            console.error(`Error fetching orders for user ${userId}:`, fallbackError);
          }
        }
      }

      // Sort all orders by date (newest first)
      allOrdersData.sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt.seconds * 1000) : new Date(a.order_date);
        const dateB = b.updatedAt ? new Date(b.updatedAt.seconds * 1000) : new Date(b.order_date);
        return dateB - dateA;
      });

      setAllOrders(allOrdersData);
      setOrders(allOrdersData);
      setHasMore(false); // No more pagination needed when all orders are loaded
      setLoading(false);
    } catch (error) {
      console.error('Error fetching all orders:', error);
      setLoading(false);
    }
  };

  // Fetching orders from Firebase with pagination and date filtering (for initial load)
  const fetchRecentOrders = async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (!isLoadMore) {
          setOrders([]);
          setAllOrders([]);
          setLastVisible(null);
          setHasMore(true);
        }
      }

      const allOrdersData = [];
      const userIds = Object.keys(allUsers);
      const newLastVisible = {};
      
      // Apply date filter only for recent orders view
      const dateFilter = getDateFilter();
      
      for (const userId of userIds) {
        let ordersQuery;
        
        if (isLoadMore && lastVisible && lastVisible[userId]) {
          // For load more, start after the last visible document for this user
          ordersQuery = query(
            collection(db, 'users', userId, 'orders'),
            where('status', '==', 'confirmed'),
            where('updatedAt', '>=', dateFilter),
            orderBy('updatedAt', 'desc'),
            startAfter(lastVisible[userId]),
            limit(ORDERS_PER_BATCH)
          );
        } else {
          // Initial load
          ordersQuery = query(
            collection(db, 'users', userId, 'orders'),
            where('status', '==', 'confirmed'),
            where('updatedAt', '>=', dateFilter),
            orderBy('updatedAt', 'desc'),
            limit(ORDERS_PER_BATCH)
          );
        }

        try {
          const ordersSnapshot = await getDocs(ordersQuery);
          
          if (ordersSnapshot.docs.length > 0) {
            // Store last visible document for this user
            const lastDoc = ordersSnapshot.docs[ordersSnapshot.docs.length - 1];
            newLastVisible[userId] = lastDoc;

            ordersSnapshot.docs.forEach(orderDoc => {
              const orderData = orderDoc.data();
              allOrdersData.push({
                id: orderDoc.id,
                userId,
                brandName: allUsers[userId]?.brandName || 'Unknown Brand',
                ...orderData,
              });
            });
          }
        } catch (userError) {
          // If updatedAt field doesn't exist, try with order_date
          console.warn(`updatedAt not found for user ${userId}, trying with order_date`);
          
          let fallbackQuery;
          const sevenDaysAgoDate = new Date();
          sevenDaysAgoDate.setDate(sevenDaysAgoDate.getDate() - 7);
          
          if (isLoadMore && lastVisible && lastVisible[userId]) {
            fallbackQuery = query(
              collection(db, 'users', userId, 'orders'),
              where('status', '==', 'confirmed'),
              where('order_date', '>=', sevenDaysAgoDate.toISOString()),
              orderBy('order_date', 'desc'),
              startAfter(lastVisible[userId]),
              limit(ORDERS_PER_BATCH)
            );
          } else {
            fallbackQuery = query(
              collection(db, 'users', userId, 'orders'),
              where('status', '==', 'confirmed'),
              where('order_date', '>=', sevenDaysAgoDate.toISOString()),
              orderBy('order_date', 'desc'),
              limit(ORDERS_PER_BATCH)
            );
          }

          try {
            const fallbackSnapshot = await getDocs(fallbackQuery);
            
            if (fallbackSnapshot.docs.length > 0) {
              const lastDoc = fallbackSnapshot.docs[fallbackSnapshot.docs.length - 1];
              newLastVisible[userId] = lastDoc;

              fallbackSnapshot.docs.forEach(orderDoc => {
                const orderData = orderDoc.data();
                allOrdersData.push({
                  id: orderDoc.id,
                  userId,
                  brandName: allUsers[userId]?.brandName || 'Unknown Brand',
                  ...orderData,
                });
              });
            }
          } catch (fallbackError) {
            console.error(`Error fetching orders for user ${userId}:`, fallbackError);
          }
        }
      }

      // Update lastVisible with the new values
      if (isLoadMore) {
        setLastVisible(prev => ({ ...prev, ...newLastVisible }));
      } else {
        setLastVisible(newLastVisible);
      }

      // Sort all orders by date (newest first)
      allOrdersData.sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt.seconds * 1000) : new Date(a.order_date);
        const dateB = b.updatedAt ? new Date(b.updatedAt.seconds * 1000) : new Date(b.order_date);
        return dateB - dateA;
      });

      if (isLoadMore) {
        const newAllOrders = [...allOrders, ...allOrdersData];
        setAllOrders(newAllOrders);
        setOrders(newAllOrders);
      } else {
        setAllOrders(allOrdersData);
        setOrders(allOrdersData);
      }

      // Check if there are more orders to load
      const hasMoreOrders = Object.values(newLastVisible).length > 0 && 
        allOrdersData.length >= ORDERS_PER_BATCH;
      setHasMore(hasMoreOrders);

      setLoading(false);
      setLoadingMore(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoading(false);
      setLoadingMore(false);
    }
  };


  const markOrderAsShipped = async () => {
  if (!selectedOrder) {
    alert("No order selected.");
    return;
  }

  setMarkingAsShipped(true);

  try {
    // Update the order in Firebase with shipping_status = "shipped"
    const orderRef = doc(db, 'users', selectedOrder.userId, 'confirmed_orders', selectedOrder.id);
    await setDoc(orderRef, {
      shipping_status: 'shipped',
      updatedAt: new Date()
    }, { merge: true });

    // Update the local state to reflect the change
    setAllOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === selectedOrder.id 
          ? { ...order, shipping_status: 'shipped'} 
          : order
      )
    );
    
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === selectedOrder.id 
          ? { ...order, shipping_status: 'shipped' } 
          : order
      )
    );

    // Update selectedOrder to show the change in modal
    setSelectedOrder(prev => ({
      ...prev,
      shipping_status: 'shipped',
      shipped_at: new Date()
    }));

    alert("Order marked as shipped successfully!");
    
  } catch (error) {
    console.error('Error marking order as shipped:', error);
    alert('Failed to mark order as shipped. Please try again.');
  } finally {
    setMarkingAsShipped(false);
  }
};


  // Load more recent orders
  const loadMoreOrders = () => {
    if (!loadingMore && hasMore && !showAllOrders) {
      fetchRecentOrders(true);
    }
  };

  // Toggle between recent orders and all orders
  const toggleOrderView = () => {
    if (showAllOrders) {
      // Switch back to recent orders
      setShowAllOrders(false);
      fetchRecentOrders(false);
    } else {
      // Load all orders for comprehensive search
      setShowAllOrders(true);
      fetchAllOrdersForSearch();
    }
  };

  // Apply search and brand filters to orders
  const applyFilters = () => {
    let filtered = allOrders;

    // Filter by search query (Order ID) - case insensitive partial match
    if (searchQuery.trim()) {
      filtered = filtered.filter(order => 
        order.id.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
        order.order_id?.toLowerCase().includes(searchQuery.trim().toLowerCase())
      );
    }

    // Filter by brand - case insensitive partial match
    if (brandFilter.trim()) {
      filtered = filtered.filter(order =>
        order.brandName.toLowerCase().includes(brandFilter.trim().toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  };

  useEffect(() => {
    // First fetch all users, then fetch initial orders
    const initializeData = async () => {
      await fetchAllUsers();
    };
    initializeData();
  }, []);

  useEffect(() => {
    // Fetch orders after users are loaded
    if (Object.keys(allUsers).length > 0) {
      fetchRecentOrders(false); // Initial load with recent orders
    }
  }, [allUsers]);

  // Apply filters whenever orders, search, or brand filter changes
  useEffect(() => {
    applyFilters();
  }, [allOrders, searchQuery, brandFilter]);

  // Handle search input change
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    
    // If user starts searching and we're not showing all orders, switch to all orders
    if (value.trim() && !showAllOrders) {
      setShowAllOrders(true);
      fetchAllOrdersForSearch();
    }
  };

  // Handle brand filter change
  const handleBrandFilterChange = (event) => {
    const value = event.target.value;
    setBrandFilter(value);
    
    // If user starts filtering and we're not showing all orders, switch to all orders
    if (value.trim() && !showAllOrders) {
      setShowAllOrders(true);
      fetchAllOrdersForSearch();
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setBrandFilter('');
  };

  // Fetch tracking data for a specific order
  const fetchTrackingData = async (orderId) => {
    try {
      setTrackingLoading(prev => ({ ...prev, [orderId]: true }));
      
      const response = await axios.get(`https://ufb-1.onrender.com/api/track-order?order_id=${orderId}`);
      
      if (response.data && response.data.tracking_url) {
        // Update the order with tracking URL in local state
        setAllOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderId 
              ? { ...order, tracking_url: response.data.tracking_url } 
              : order
          )
        );
        
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderId 
              ? { ...order, tracking_url: response.data.tracking_url } 
              : order
          )
        );
        
        // Open the tracking URL in a new tab
        window.open(response.data.tracking_url, '_blank');
        
        return {
          tracking_url: response.data.tracking_url,
        };
      }
    } catch (error) {
      console.error("Error fetching tracking data for order", orderId, error);
      alert("Failed to fetch tracking information. Please try again.");
    } finally {
      setTrackingLoading(prev => ({ ...prev, [orderId]: false }));
    }
    return { tracking_url: null };
  };

  // Open modal with selected order details
  const openModal = (order) => {
    setSelectedOrder(order);
    setSelectedItems([]);
    setIsModalOpen(true);
  };

  // Close the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
    setSelectedItems([]);
  };

  // Handle item selection for duplicate order
  const handleItemSelection = (itemIndex) => {
    setSelectedItems(prev => {
      if (prev.includes(itemIndex)) {
        return prev.filter(index => index !== itemIndex);
      } else {
        return [...prev, itemIndex];
      }
    });
  };

  // Create duplicate order with selected items
  const createReplacementOrder = async () => {
    if (!selectedOrder || selectedItems.length === 0) {
      alert("Please select at least one item to create a duplicate order.");
      return;
    }

    setCreatingDuplicate(true);

    try {
      const selectedOrderItems = selectedItems.map(index => selectedOrder.order_items[index]);
      
      // Calculate new total price
      const newTotalPrice = selectedOrderItems.reduce((total, item) => {
        return total + (item.selling_price * item.units);
      }, 0);

      const duplicateOrderId = `REP_${selectedOrder.id}`;
      
      // Create clean duplicate order data without tracking information
      const duplicateOrder = {
        // Basic order info
        order_id: duplicateOrderId,
        order_date: new Date().toISOString(),
        updatedAt: new Date(),
        status: 'pending', // Will be updated to 'confirmed' after API call
        original_order_id: selectedOrder.id,
        is_replacement: true,
        
        // Customer and billing info
        billing_customer_name: selectedOrder.billing_customer_name || '',
        billing_last_name: selectedOrder.billing_last_name || '',
        billing_email: selectedOrder.billing_email || '',
        billing_phone: selectedOrder.billing_phone || '',
        billing_address: selectedOrder.billing_address || '',
        billing_address_2: selectedOrder.billing_address_2 || '',
        billing_city: selectedOrder.billing_city|| '',
        billing_state: selectedOrder.billing_state || '',
        billing_country: selectedOrder.billing_country || '',
        billing_pincode: selectedOrder.billing_pincode || '',
        
        // Shipping info (same as billing)
        shipping_customer_name: selectedOrder.shipping_customer_name || selectedOrder.billing_customer_name,
        shipping_last_name: selectedOrder.shipping_last_name || selectedOrder.billing_last_name || '',
        shipping_email: selectedOrder.shipping_email || selectedOrder.billing_email,
        shipping_phone: selectedOrder.shipping_phone || selectedOrder.billing_phone,
        shipping_address: selectedOrder.shipping_address || selectedOrder.billing_address,
        shipping_address_2: selectedOrder.shipping_address_2 || selectedOrder.billing_address_2 || '',
        shipping_city: selectedOrder.shipping_city || selectedOrder.billing_city,
        shipping_state: selectedOrder.shipping_state || selectedOrder.billing_state,
        shipping_country: selectedOrder.shipping_country || selectedOrder.billing_country,
        shipping_pincode: selectedOrder.shipping_pincode || selectedOrder.billing_pincode,
        
        // Order items and pricing
        order_items: selectedOrderItems,
        UniteeTotalPrice: 0,
        total: newTotalPrice,
        
        // Payment info
        payment_method: 'Prepaid',
        financial_status: 'paid',
        total_outstanding: selectedOrder.total_outstanding || newTotalPrice,
        
        // Package dimensions (if available)
        length: selectedOrder.length || 10,
        breadth: selectedOrder.breadth || 10,
        height: selectedOrder.height || 5,
        weight: selectedOrder.weight || 0.5,
        
        // Additional info
        comment: `Replacement order for ${selectedOrder.id}`,
        
        // DO NOT include tracking related fields
        // tracking_url, awb_code, shipment_id, etc. will be added by API
      };

      // First save the order to Firebase with pending status
      const orderRef = doc(db, 'users', selectedOrder.userId, 'orders', duplicateOrderId);
      await setDoc(orderRef, duplicateOrder);

      // Prepare data for Shiprocket API
      const shiprocketData = {
        user_uid: selectedOrder.userId,
        order_id: duplicateOrderId,
        order_date: new Date().toISOString(),
        shipping_customer_name: duplicateOrder.shipping_customer_name,
        shipping_last_name: duplicateOrder.shipping_last_name,
        shipping_address: duplicateOrder.shipping_address,
        shipping_address_2: duplicateOrder.shipping_address_2,
        shipping_city: duplicateOrder.shipping_city,
        shipping_pincode: duplicateOrder.shipping_pincode,
        shipping_state: duplicateOrder.shipping_state,
        shipping_country: duplicateOrder.shipping_country,
        shipping_email: duplicateOrder.shipping_email,
        shipping_phone: duplicateOrder.shipping_phone,
        billing_customer_name: duplicateOrder.billing_customer_name,
        billing_last_name: duplicateOrder.billing_last_name,
        billing_address: duplicateOrder.billing_address,
        billing_address_2: duplicateOrder.billing_address_2,
        billing_city: duplicateOrder.billing_city,
        billing_pincode: duplicateOrder.billing_pincode,
        billing_state: duplicateOrder.billing_state,
        billing_country: duplicateOrder.billing_country,
        billing_email: duplicateOrder.billing_email,
        billing_phone: duplicateOrder.billing_phone,
        order_items: selectedOrderItems,
        payment_method: "Prepaid",
        financial_status: "paid",
        total: newTotalPrice,
        total_outstanding: duplicateOrder.total_outstanding,
        length: duplicateOrder.length,
        breadth: duplicateOrder.breadth,
        height: duplicateOrder.height,
        weight: duplicateOrder.weight,
        comment: duplicateOrder.comment,
        shopDomain: selectedOrder.shopDomain || 'admin-replacement'
      };

      console.log('Calling Shiprocket API with data:', shiprocketData);

      // Call Shiprocket API to create the order
      const shiprocketResponse = await axios.post('https://ufb-1.onrender.com/create-shipmozo-order', shiprocketData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('Shiprocket API response:', shiprocketResponse.data);

      // Update the order status to confirmed
      await setDoc(orderRef, {
        ...duplicateOrder,
        status: 'confirmed',
        updatedAt: new Date()
      }, { merge: true });

      alert(`Duplicate order created successfully with ID: ${duplicateOrderId}`);
      
      // Refresh orders to include the new duplicate
      await fetchAllUsers(); // Refresh users first
      if (showAllOrders) {
        fetchAllOrdersForSearch(); // Fetch all orders to include the new duplicate
      } else {
        fetchRecentOrders(false); // Fetch recent orders
      }
      closeModal();
      
    } catch (error) {
      console.error('Error creating duplicate order:', error);
      
      // If Shiprocket API fails, update order status to failed
      try {
        const orderRef = doc(db, 'users', selectedOrder.userId, 'orders', `REP_${selectedOrder.id}`);
        await setDoc(orderRef, {
          status: 'failed',
          error_message: error.message,
          updatedAt: new Date()
        }, { merge: true });
      } catch (updateError) {
        console.error('Error updating failed order status:', updateError);
      }
      
      if (error.response) {
        alert(`Failed to create duplicate order: ${error.response.data?.error || error.message}`);
      } else {
        alert('Failed to create duplicate order. Please try again.');
      }
    } finally {
      setCreatingDuplicate(false);
    }
  };



    const createDuplicateOrder = async () => {
    if (!selectedOrder || selectedItems.length === 0) {
      alert("Please select at least one item to create a duplicate order.");
      return;
    }

    setCreatingDuplicate(true);

    try {
      const selectedOrderItems = selectedItems.map(index => selectedOrder.order_items[index]);
      
      // Calculate new total price
      const newTotalPrice = selectedOrderItems.reduce((total, item) => {
        return total + (item.selling_price * item.units);
      }, 0);

      const duplicateOrderId = `MaroonAW_${selectedOrder.id}`;
      
      // Create clean duplicate order data without tracking information
      const duplicateOrder = {
        // Basic order info
        order_id: duplicateOrderId,
        order_date: new Date().toISOString(),
        updatedAt: new Date(),
        status: 'pending', // Will be updated to 'confirmed' after API call
        original_order_id: selectedOrder.id,
        is_replacement: true,
        
        // Customer and billing info
        billing_customer_name: selectedOrder.billing_customer_name,
        billing_last_name: selectedOrder.billing_last_name || '',
        billing_email: selectedOrder.billing_email,
        billing_phone: selectedOrder.billing_phone,
        billing_address: selectedOrder.billing_address,
        billing_address_2: selectedOrder.billing_address_2 || '',
        billing_city: selectedOrder.billing_city,
        billing_state: selectedOrder.billing_state,
        billing_country: selectedOrder.billing_country,
        billing_pincode: selectedOrder.billing_pincode,
        
        // Shipping info (same as billing)
        shipping_customer_name: selectedOrder.shipping_customer_name || selectedOrder.billing_customer_name,
        shipping_last_name: selectedOrder.shipping_last_name || selectedOrder.billing_last_name || '',
        shipping_email: selectedOrder.shipping_email || selectedOrder.billing_email,
        shipping_phone: selectedOrder.shipping_phone || selectedOrder.billing_phone,
        shipping_address: selectedOrder.shipping_address || selectedOrder.billing_address,
        shipping_address_2: selectedOrder.shipping_address_2 || selectedOrder.billing_address_2 || '',
        shipping_city: selectedOrder.shipping_city || selectedOrder.billing_city,
        shipping_state: selectedOrder.shipping_state || selectedOrder.billing_state,
        shipping_country: selectedOrder.shipping_country || selectedOrder.billing_country,
        shipping_pincode: selectedOrder.shipping_pincode || selectedOrder.billing_pincode,
        
        // Order items and pricing
        order_items: selectedOrderItems,
        UniteeTotalPrice: 0,
        total: newTotalPrice,
        
        // Payment info
        payment_method: 'Prepaid',
        financial_status: 'paid',
        total_outstanding: selectedOrder.total_outstanding || newTotalPrice,
        
        // Package dimensions (if available)
        length: selectedOrder.length || 10,
        breadth: selectedOrder.breadth || 10,
        height: selectedOrder.height || 5,
        weight: selectedOrder.weight || 0.5,
        
        // Additional info
        comment: `Replacement order for ${selectedOrder.id}`,
        
        // DO NOT include tracking related fields
        // tracking_url, awb_code, shipment_id, etc. will be added by API
      };

      // First save the order to Firebase with pending status
      const orderRef = doc(db, 'users', selectedOrder.userId, 'orders', duplicateOrderId);
      await setDoc(orderRef, duplicateOrder);

      // Prepare data for Shiprocket API
      const shiprocketData = {
        user_uid: selectedOrder.userId,
        order_id: duplicateOrderId,
        order_date: new Date().toISOString(),
        shipping_customer_name: duplicateOrder.shipping_customer_name,
        shipping_last_name: duplicateOrder.shipping_last_name,
        shipping_address: duplicateOrder.shipping_address,
        shipping_address_2: duplicateOrder.shipping_address_2,
        shipping_city: duplicateOrder.shipping_city,
        shipping_pincode: duplicateOrder.shipping_pincode,
        shipping_state: duplicateOrder.shipping_state,
        shipping_country: duplicateOrder.shipping_country,
        shipping_email: duplicateOrder.shipping_email,
        shipping_phone: duplicateOrder.shipping_phone,
        billing_customer_name: duplicateOrder.billing_customer_name,
        billing_last_name: duplicateOrder.billing_last_name,
        billing_address: duplicateOrder.billing_address,
        billing_address_2: duplicateOrder.billing_address_2,
        billing_city: duplicateOrder.billing_city,
        billing_pincode: duplicateOrder.billing_pincode,
        billing_state: duplicateOrder.billing_state,
        billing_country: duplicateOrder.billing_country,
        billing_email: duplicateOrder.billing_email,
        billing_phone: duplicateOrder.billing_phone,
        order_items: selectedOrderItems,
        payment_method: "Prepaid",
        financial_status: "paid",
        total: newTotalPrice,
        total_outstanding: duplicateOrder.total_outstanding,
        length: duplicateOrder.length,
        breadth: duplicateOrder.breadth,
        height: duplicateOrder.height,
        weight: duplicateOrder.weight,
        comment: duplicateOrder.comment,
        shopDomain: selectedOrder.shopDomain || 'admin-replacement'
      };

      console.log('Calling Shiprocket API with data:', shiprocketData);

      // Call Shiprocket API to create the order
      const shiprocketResponse = await axios.post('https://ufb-1.onrender.com/create-shipmozo-order', shiprocketData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('Shiprocket API response:', shiprocketResponse.data);

      // Update the order status to confirmed
      await setDoc(orderRef, {
        ...duplicateOrder,
        status: 'confirmed',
        updatedAt: new Date()
      }, { merge: true });

      alert(`Duplicate order created successfully with ID: ${duplicateOrderId}`);
      
      // Refresh orders to include the new duplicate
      await fetchAllUsers(); // Refresh users first
      if (showAllOrders) {
        fetchAllOrdersForSearch(); // Fetch all orders to include the new duplicate
      } else {
        fetchRecentOrders(false); // Fetch recent orders
      }
      closeModal();
      
    } catch (error) {
      console.error('Error creating duplicate order:', error);
      
      // If Shiprocket API fails, update order status to failed
      try {
        const orderRef = doc(db, 'users', selectedOrder.userId, 'orders', `MaroonAW_${selectedOrder.id}`);
        await setDoc(orderRef, {
          status: 'failed',
          error_message: error.message,
          updatedAt: new Date()
        }, { merge: true });
      } catch (updateError) {
        console.error('Error updating failed order status:', updateError);
      }
      
      if (error.response) {
        alert(`Failed to create duplicate order: ${error.response.data?.error || error.message}`);
      } else {
        alert('Failed to create duplicate order. Please try again.');
      }
    } finally {
      setCreatingDuplicate(false);
    }
  };

  const formatDate = (order) => {
    if (order.updatedAt && order.updatedAt.seconds) {
      return new Date(order.updatedAt.seconds * 1000).toLocaleDateString();
    }
    return new Date(order.order_date).toLocaleDateString();
  };

  return (
    <div className="admin-orders-page">
      <h1>Admin Orders</h1>
      
      {/* Toggle between recent and all orders */}
      <div style={{ marginBottom: '15px' }}>
        {/* <button
          onClick={toggleOrderView}
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: showAllOrders ? '#28a745' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginRight: '10px'
          }}
        >
          {loading ? 'Loading...' : (showAllOrders ? 'Showing All Orders' : 'Show All Orders (for Search)')}
        </button> */}
        
        {/* {!showAllOrders && (
          <span style={{ fontSize: '14px', color: '#666' }}>
            Currently showing orders from last 7 days. Click "Show All Orders" to search through all orders.
          </span>
        )}
        
        {showAllOrders && (
          <span style={{ fontSize: '14px', color: '#28a745' }}>
            ✓ All orders loaded - You can now search and filter through all orders
          </span>
        )} */}
      </div>

      {/* Search and Filter container */}
      <div className="search-filter-container" style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by Order ID"
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '200px' }}
          />
        </div>
        
        <div className="brand-filter-container">
          <select
            value={brandFilter}
            onChange={handleBrandFilterChange}
            className="brand-filter-select"
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="">All Brands</option>
            {availableBrands.map(brand => (
              <option key={brand} value={brand}>{brand}</option>
            ))}
          </select>
        </div>
        
        {(searchQuery || brandFilter) && (
          <button
            onClick={clearFilters}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Results summary */}
      {/* {!loading && (
        <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
          {searchQuery || brandFilter ? (
            <p>
              Showing {filteredOrders.length} filtered orders out of {allOrders.length} total orders
              {searchQuery && ` (Search: "${searchQuery}")`}
              {brandFilter && ` (Brand: "${brandFilter}")`}
            </p>
          ) : (
            <p></p>
          )}
        </div>
      )} */}

      {loading ? (
        <p>Loading orders...</p>
      ) : (
        <>
          <table className="admin-orders-table">
            <thead>
              <tr>
                <th>Order No</th>
                <th>Brand Name</th>
                <th>Total Price</th>
                <th>Order Date</th>
                <th>Track Order</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => (
                <tr key={order.id}>
                  <td 
                    onClick={() => openModal(order)}
                    style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                  >
                    {order.id}
                  </td>
                  <td>{order.brandName}</td>
                  <td>₹{order.UniteeTotalPrice}</td>
                  <td>{formatDate(order)}</td>
                  <td>
                    <button
                      onClick={() => fetchTrackingData(order.id)}
                      disabled={trackingLoading[order.id]}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: trackingLoading[order.id] ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {trackingLoading[order.id] ? 'Loading...' : 'Track Order'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Load More Button - only show when not showing all orders and no filters are applied */}
          {!showAllOrders && hasMore && !loading && !searchQuery && !brandFilter && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={loadMoreOrders}
                disabled={loadingMore}
                style={{
                  padding: '10px 20px',
                  backgroundColor: loadingMore ? '#ccc' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loadingMore ? 'not-allowed' : 'pointer',
                  fontSize: '16px'
                }}
              >
                {loadingMore ? 'Loading More...' : 'Load More Recent Orders'}
              </button>
            </div>
          )}
          
          {/* Show end message */}
          {/* {((showAllOrders && !loading) || (!hasMore && orders.length > 0)) && (
            <div style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
              <p>
                {showAllOrders 
                  ? `All ${allOrders.length} orders loaded` 
                  : 'No more recent orders to load'
                }
              </p>
            </div>
          )} */}

          {/* No results message */}
          {!loading && filteredOrders.length === 0 && allOrders.length > 0 && (searchQuery || brandFilter) && (
            <div style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
              <p>No orders found matching your search criteria.</p>
            </div>
          )}
        </>
      )}

      {/* Modal for showing order details */}
    

      {/* Modal for showing order details */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Order Details"
        ariaHideApp={false}
        className="modal"
        overlayClassName="overlay"
      >
        <h2>Order Details</h2>
        {selectedOrder && (
          <div className="order-details">
            <p><strong>Customer Name:</strong> {selectedOrder.billing_customer_name}</p>
            <p><strong>Brand Name:</strong> {selectedOrder.brandName}</p>
            <p><strong>Email:</strong> {selectedOrder.billing_email}</p>
            <p><strong>Phone:</strong> {selectedOrder.billing_phone}</p>
            <p><strong>Address:</strong> {selectedOrder.billing_address}</p>
            <p><strong>City:</strong> {selectedOrder.billing_city}</p>
            <p><strong>State:</strong> {selectedOrder.billing_state}</p>
            <p><strong>Country:</strong> {selectedOrder.billing_country}</p>
            <p><strong>Pincode:</strong> {selectedOrder.billing_pincode}</p>

            <h4>Order Items:</h4>
            {/* Ensure order_items is an array before mapping */}
            {Array.isArray(selectedOrder.order_items) && selectedOrder.order_items.length > 0 ? (
              <div>
                {selectedOrder.order_items.map((item, index) => (
                  <div key={index} style={{ border: '1px solid #ddd', padding: '10px', margin: '5px 0', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(index)}
                        onChange={() => handleItemSelection(index)}
                      />
                      <div>
                        <p><strong>Design Name:</strong> {item.name}</p>
                        <p><strong>SKU:</strong> {item.sku}</p>
                        <p><strong>Price:</strong> ₹{item.selling_price}</p>
                        <p><strong>Size:</strong> {item.size}</p>
                        <p><strong>Units:</strong> {item.units}</p>
                      </div>
                    </div>
                  </div>
                ))}


                <div style={{ marginTop: '15px', borderTop: '1px solid #ddd', paddingTop: '15px' }}>
  <button
    onClick={markOrderAsShipped}
    disabled={markingAsShipped || selectedOrder?.shipping_status === 'shipped'}
    style={{
      padding: '10px 20px',
      backgroundColor: selectedOrder?.shipping_status === 'shipped' ? '#28a745' : '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: markingAsShipped || selectedOrder?.shipping_status === 'shipped' ? 'not-allowed' : 'pointer',
      marginRight: '10px'
    }}
  >
    {markingAsShipped ? 'Marking as Shipped...' : 
     selectedOrder?.shipping_status === 'shipped' ? '✓ Already Shipped' : 'Mark as Shipped'}
  </button>
</div>
                
                <div style={{ marginTop: '15px' }}>
                  <button
                    onClick={createReplacementOrder}
                    disabled={selectedItems.length === 0 || creatingDuplicate}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: selectedItems.length === 0 ? '#ccc' : '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedItems.length === 0 || creatingDuplicate ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {creatingDuplicate ? 'Creating...' : 'Create Replacement Order'}
                  </button>

                   <button
                    onClick={createDuplicateOrder}
                    disabled={selectedItems.length === 0 || creatingDuplicate}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: selectedItems.length === 0 ? '#ccc' : '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedItems.length === 0 || creatingDuplicate ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {creatingDuplicate ? 'Creating...' : 'Create Duplicate Order Maroon AW '}
                  </button>

                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Select items above to create a replacement order
                  </p>
                </div>
              </div>
            ) : (
              <p>No items available for this order.</p>
            )}


            

            <h4>Order Information:</h4>
            <p><strong>Order ID:</strong> {selectedOrder.order_id}</p>
            <p><strong>Order Date:</strong> {new Date(selectedOrder.order_date).toLocaleString()}</p>
            <p><strong>Payment Method:</strong> {selectedOrder.payment_method}</p>
            <p><strong>Total Price:</strong> ₹{selectedOrder.UniteeTotalPrice}</p>
            {selectedOrder.invoiceUrl && (
              <p><strong>Invoice:</strong> <a href={selectedOrder.invoiceUrl} target="_blank" rel="noopener noreferrer">View Invoice</a></p>
            )}
          </div>
        )}
        <button onClick={closeModal} style={{ marginTop: '20px', padding: '10px 20px' }}>Close</button>
      </Modal>
    </div>
  );
};

export default OngoingOrders;